import _ from 'lodash';
import produce, { isDraft, original } from 'immer';
import { COMPONENT_STATES } from 'common/constants';
import { UNIFORM_PROPS } from 'common/constants/component-props';
import type { BBOtherProp, BBStates, BBStylesProp, ComputedComponentPropsFn, ComputedValueFn } from 'types/story';

/**
 * Get value computed from component state and current media query
 *
 * @param props.source component object or component property value
 * @param props.sourceKey component property key
 * @param props.mediaKeysInOrderOfInheritance media query keys array in order of inheritance
 * @param props.currState current state key
 */
export const getComputedValue: ComputedValueFn = props => {
	const { source, sourceKey = '', mediaKeysInOrderOfInheritance, currState } = props;
	const currentStates: BBStates[] = [];

	// 💡If you're adding a new compound state,
	// you've to add his implementation in "generateElementCssString()", and traverseTree too
	const compoundStates = [COMPONENT_STATES.SELECTED_HOVER] as BBStates[];
	if (compoundStates.includes(currState)) {
		switch (currState) {
			case COMPONENT_STATES.SELECTED_HOVER:
				currentStates.push(COMPONENT_STATES.SELECTED, COMPONENT_STATES.HOVER, COMPONENT_STATES.SELECTED_HOVER);
				break;
			default:
				break;
		}
	} else {
		currentStates.push(currState);
	}
	const orderedStates = _.uniq(_.concat(COMPONENT_STATES.DEFAULT, currentStates));

	let value!: ReturnType<ComputedValueFn>['value'];
	let inheritance: ReturnType<ComputedValueFn>['inheritance'];
	const hasValue = (v: unknown) => v !== '' && v !== undefined && v !== null;

	if (sourceKey in UNIFORM_PROPS && source !== undefined) {
		// Universal properties (not written for state or media query and without inheritance)
		value = source;
		inheritance = [{ value: source }];
	} else {
		const isOther = sourceKey === 'other';
		const isStyles = sourceKey === 'styles';
		const isAnimation = sourceKey === 'animation';
		const isTextChildren = sourceKey === 'textChildren';
		const canInherit = isStyles || isOther;
		const defaultValue = isStyles || isOther || isAnimation ? {} : undefined;

		_.forEach(orderedStates, state => {
			_.forEach(mediaKeysInOrderOfInheritance, platform => {
				const nextValue = _.get(source, [state, platform], defaultValue);

				if (
					(isTextChildren
						? nextValue === undefined /* accept empty string as valid value to inherit from */
						: !hasValue(nextValue)) ||
					(isAnimation && Object.entries(nextValue!).length === 0)
				) {
					return;
				}

				// Regular properties (e.g. styles, other).
				// Checks each key in nextValue and store value and inheritance for it
				if (canInherit) {
					// initialize
					if (!value) value = {};

					_.forOwn(nextValue as BBStylesProp | BBOtherProp, (v, k) => {
						// initialize
						if (!inheritance) inheritance = {};

						// Store inheritance
						const ik = inheritance[k] ?? [];
						if (hasValue(v)) {
							ik.push({ secondValue: value?.[k], value: v, state, mq: platform });
						}
						_.set(inheritance, k, ik);

						// Store computed value
						_.set(value as object, k, v);
					});
				}
				// Irregular properties (e.g. animation or Text BB children).
				// Entire nextValue object should be inherited, not each key|value separately like in 'styles|other'
				else {
					// Store inheritance
					if (!inheritance) inheritance = [];

					if (hasValue(nextValue) && Array.isArray(inheritance)) {
						inheritance.push({
							secondValue: value,
							value: nextValue,
							state,
							mq: platform,
						});
					}

					// Store computed value
					value = nextValue;
				}
			});
		});
	}

	return {
		value,
		inheritance,
	};
};

/**
 * Get computed values of Card element "uiConfig.componentProps" object
 *
 * @param element Card element
 * @param state current state of the element
 * @param mediaKeysInOrderOfInheritance ordered story media query keys in order of inheritance
 */
export const getComputedComponentProps: ComputedComponentPropsFn = ({
	element,
	mediaKeysInOrderOfInheritance,
	state,
}) => {
	const inheritance: ReturnType<ComputedComponentPropsFn>['inheritance'] = {
		styles: {},
		other: {},
	};

	const componentProps = produce(element.uiConfig.componentProps, draft => {
		_.forOwn(draft, (value, key) => {
			const computedValue = getComputedValue({
				source: isDraft(value) ? original(value)! : value,
				// @ts-ignore FIXME
				sourceKey: isDraft(key) ? original(key)! : key,
				mediaKeysInOrderOfInheritance,
				currState: state,
			});

			// Store computed value
			draft[key] = computedValue.value;

			// Store inheritance data
			inheritance[key] = computedValue.inheritance;
		});
	});

	return {
		value: componentProps,
		inheritance,
	};
};
